import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllReports, getAllBrands, exportReport } from '../../store/actions';
import { useToast } from '../ui/Toast';
import ReportViewer from './ReportViewer';

const CATEGORIES = ['Financial', 'Operational', 'Compliance'];

/* -- Client-side XLS generator --------------------------------------------- */
async function generateXLS(data, fileName) {
  const xlsxModule = await import(/* webpackChunkName: "xlsx" */ 'xlsx');
  const XLSX = xlsxModule?.default ?? xlsxModule;

  const sheetRows = [
    data.headers || [],
    ...(data.rows || []).map((r) => r.map((c) => (c === null || c === undefined ? '' : c))),
  ];
  const ws = XLSX.utils.aoa_to_sheet(sheetRows);
  if (data.headers) {
    ws['!cols'] = data.headers.map((h, i) => ({
      wch: Math.max(h.length, ...(data.rows || []).map((r) => String(r[i] ?? '').length)) + 2,
    }));
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

/* -- Client-side PDF generator --------------------------------------------- */
async function generatePDF(data, fileName) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(data.reportName || fileName, 14, 16);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(`Generated: ${data.generatedAt || new Date().toISOString().slice(0, 10)}`, 14, 23);
  doc.setTextColor(0);

  let cursorY = 30;

  // Summary table (only if non-zero summary exists)
  const hasSummary =
    data.summary &&
    Object.keys(data.summary).length > 0 &&
    Object.values(data.summary).some((v) => v !== null && v !== undefined && Number(v) !== 0);

  if (hasSummary) {
    const entries = Object.entries(data.summary);
    autoTable(doc, {
      startY: cursorY,
      head: [entries.map(([k]) => k.replace(/([A-Z])/g, ' $1').trim())],
      body: [entries.map(([, v]) => String(v ?? ''))],
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [27, 58, 107], textColor: 255, fontStyle: 'bold' },
      margin: { left: 14, right: 14 },
    });
    cursorY = doc.lastAutoTable.finalY + 6;
  }

  // Data table
  const cleanRows = (data.rows || []).map((r) =>
    r.map((c) => (c === null || c === undefined ? '-' : String(c)))
  );

  autoTable(doc, {
    startY: cursorY,
    head: [data.headers || []],
    body: cleanRows.length > 0 ? cleanRows : [['No data available for the selected filters.']],
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [27, 58, 107], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 14, right: 14 },
  });

  doc.save(`${fileName}.pdf`);
}

/* -- Safe filename --------------------------------------------------------- */
const toFileName = (name) => (name || 'report').replace(/[^a-z0-9]/gi, '_');

/* -- Main component -------------------------------------------------------- */
export default function ReportCatalog() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const reports = useSelector((s) => s.report.reports);
  const loading = useSelector((s) => s.report.loading);
  const brands = useSelector((s) => s.brand.brands);

  const [brandFilter, setBrandFilter] = useState('');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [busyReportId, setBusyReportId] = useState(null);
  const [viewer, setViewer] = useState(null); // { report, data, loading }

  useEffect(() => {
    dispatch(getAllReports());
    dispatch(getAllBrands());
  }, [dispatch]);

  const grouped = useMemo(
    () =>
      CATEGORIES.map((cat) => ({
        cat,
        items: reports.filter((r) => r.category === cat),
      })).filter((g) => g.items.length > 0),
    [reports],
  );

  const fetchAndExport = (report, handler) => {
    setBusyReportId(report.id);
    dispatch(
      exportReport({
        payload: { id: report.id, format: 'json', brandId: brandFilter, dateRange },
        callback: {
          success: async (data) => {
            try {
              await handler(data);
            } catch (e) {
              console.error(e);
              showToast('File generation failed. Check console.', 'error');
            } finally {
              setBusyReportId(null);
            }
          },
          error: (err) => {
            setBusyReportId(null);
            showToast(err || 'Failed to fetch report data', 'error');
          },
        },
      }),
    );
  };

  const handlePDF = (report) =>
    fetchAndExport(report, (data) => generatePDF(data, toFileName(report.name)));

  const handleXLS = (report) =>
    fetchAndExport(report, (data) => generateXLS(data, toFileName(report.name)));

  const handleView = (report) => {
    setViewer({ report, data: null, loading: true });
    dispatch(
      exportReport({
        payload: { id: report.id, format: 'json', brandId: brandFilter, dateRange },
        callback: {
          success: (data) => setViewer({ report, data, loading: false }),
          error: (err) => {
            setViewer(null);
            showToast(err || 'Failed to fetch report data', 'error');
          },
        },
      }),
    );
  };

  return (
    <>
      {/* Filter row */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <select
          value={brandFilter}
          onChange={(e) => setBrandFilter(e.target.value)}
          className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none"
        >
          <option value="">All Brands</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>

        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none"
        >
          <option value="Last 30 days">Last 30 days</option>
          <option value="Last 90 days">Last 90 days</option>
          <option value="Current FY">Current FY</option>
          <option value="Custom range">Custom range</option>
        </select>

        {(loading || busyReportId !== null) && (
          <span className="text-[11px] text-[#1b3a6b] font-medium animate-pulse">
            {busyReportId !== null ? 'Generating...' : 'Loading...'}
          </span>
        )}
      </div>

      {/* Empty state */}
      {!loading && reports.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-100 py-12 text-center text-[12px] text-gray-400">
          No reports configured yet.
        </div>
      )}

      {/* Report cards */}
      {grouped.map(({ cat, items }) => (
        <div key={cat} className="mb-8">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">
            {cat} Reports
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((report) => {
              const isBusy = busyReportId === report.id;
              return (
                <div
                  key={report.id}
                  className="bg-white rounded-lg border border-gray-100 p-4 hover:shadow-sm transition-shadow flex flex-col justify-between"
                >
                  <div>
                    <h3 className="text-[13px] font-semibold text-gray-900 mb-1">{report.name}</h3>
                    <p className="text-[12px] text-gray-500 mb-4">{report.desc}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleView(report)}
                      disabled={busyReportId !== null}
                      title="View report"
                      className="flex-1 py-1.5 text-[11px] font-medium text-[#1b3a6b] border border-[#1b3a6b]/30 rounded-lg hover:bg-blue-50 disabled:opacity-40 transition-colors text-center"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handlePDF(report)}
                      disabled={busyReportId !== null}
                      title="Download PDF"
                      className="flex-1 py-1.5 text-[11px] font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 transition-colors text-center"
                    >
                      {isBusy ? 'Generating...' : 'PDF'}
                    </button>
                    <button
                      onClick={() => handleXLS(report)}
                      disabled={busyReportId !== null}
                      title="Download Excel"
                      className="flex-1 py-1.5 text-[11px] font-medium text-green-700 border border-green-200 rounded-lg hover:bg-green-50 disabled:opacity-40 transition-colors text-center"
                    >
                      {isBusy ? 'Generating...' : 'XLS'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <ReportViewer
        open={!!viewer}
        onClose={() => setViewer(null)}
        reportName={viewer?.report?.name}
        data={viewer?.data}
        loading={!!viewer?.loading}
      />
    </>
  );
}
